import type { SSEDrop } from '@/api/models/SSEDrop'

export type ArenaUiPhase = 'LOADING' | 'WAITING' | 'PLAYING' | 'FINISHED'

export type ArenaRoomStatus = 'WAITING' | 'READY' | 'PLAYING' | 'COMPLETED' | number | null | undefined

export interface ArenaCase {
  id: string
  name: string
  image: string
  itemImage?: string
  price: number
}

export interface ArenaParticipant {
  id: string
  roomId?: string
  userId?: string
  name: string
  avatar?: string
  steamId?: string
  seed?: string
  clientSeed?: string
  isBot?: boolean
  isReady?: boolean
  team?: number
  slot?: number
  totalOpened?: number
  totalWinnings?: number
  wonValue?: number
  winner?: boolean
  hasWon?: boolean
}

export interface ArenaRoom {
  id: string
  shortId: string
  creatorId?: string
  isPrivate?: boolean
  isSponsored?: boolean
  isTokenRoom?: boolean
  isPve?: boolean
  status?: ArenaRoomStatus
  slots?: number
  slotsPerTeam?: number
  joinedUsers?: string[]
  cost?: number
  fullCost?: number
  rounds?: number
  currentRound?: number
  caseIds?: string[]
  cases?: ArenaCase[]
  workerId?: string | null
  randomSeed?: string | null
  randomId?: string | null
  startedAt?: string | null
  participants?: ArenaParticipant[]
  referralCode?: string | null
  upfrontPayPerSlot?: number
  createdAt?: string
  updatedAt?: string
}

export interface ArenaRoundInfo {
  id?: string
  roomId?: string
  caseId?: string
  round?: number
  createdAt?: string
  updatedAt?: string
}

export interface ArenaRoomDetailsResponse {
  room?: ArenaRoom
  participants?: ArenaParticipant[]
  rolls?: SSEDrop[]
  wonItems?: Record<string, Array<Record<string, unknown>>>
}

export interface ArenaListResponse {
  rooms?: ArenaRoom[]
}

export interface ArenaCreateRequest {
  slots: number
  caseIds: string[]
  upfrontPayPerSlot: number
  isSponsored: boolean
  isPrivate: boolean
  isTokenRoom: boolean
  isPve: boolean
}

export interface ArenaJoinRequest {
  slot: number
}

export function normalizeArenaStatus(status: ArenaRoomStatus): 'WAITING' | 'READY' | 'PLAYING' | 'COMPLETED' {
  if (status === 'WAITING' || status === 0) return 'WAITING'
  if (status === 'READY' || status === 1) return 'READY'
  if (status === 'PLAYING' || status === 2) return 'PLAYING'
  return 'COMPLETED'
}

export function getArenaUiPhase(status: ArenaRoomStatus): ArenaUiPhase {
  const normalized = normalizeArenaStatus(status)
  if (normalized === 'PLAYING') return 'PLAYING'
  if (normalized === 'COMPLETED') return 'FINISHED'
  return 'WAITING'
}

export function extractArenaParticipant(raw: any): ArenaParticipant {
  return {
    id: String(raw?.id ?? ''),
    roomId: raw?.roomId,
    userId: raw?.userId ?? raw?.user?.id,
    name: raw?.name ?? raw?.user?.name ?? 'Unknown',
    avatar: raw?.avatar ?? raw?.user?.avatar,
    steamId: raw?.steamId,
    seed: raw?.seed,
    clientSeed: raw?.clientSeed,
    isBot: Boolean(raw?.isBot),
    isReady: Boolean(raw?.isReady),
    team: raw?.team,
    slot: raw?.slot,
    totalOpened: raw?.totalOpened,
    totalWinnings: raw?.totalWinnings,
    wonValue: raw?.wonValue,
    winner: raw?.winner,
    hasWon: raw?.hasWon,
  }
}

export function extractArenaRoom(raw: any): ArenaRoom {
  return {
    id: String(raw?.id ?? ''),
    shortId: String(raw?.shortId ?? ''),
    creatorId: raw?.creatorId,
    isPrivate: Boolean(raw?.isPrivate),
    isSponsored: Boolean(raw?.isSponsored),
    isTokenRoom: Boolean(raw?.isTokenRoom),
    isPve: Boolean(raw?.isPve),
    status: raw?.status,
    slots: raw?.slots,
    slotsPerTeam: raw?.slotsPerTeam,
    joinedUsers: Array.isArray(raw?.joinedUsers) ? raw.joinedUsers : undefined,
    cost: raw?.cost,
    fullCost: raw?.fullCost,
    rounds: raw?.rounds,
    currentRound: raw?.currentRound,
    caseIds: Array.isArray(raw?.caseIds) ? raw.caseIds : undefined,
    workerId: raw?.workerId,
    randomSeed: raw?.randomSeed,
    randomId: raw?.randomId,
    startedAt: raw?.startedAt,
    referralCode: raw?.referralCode,
    upfrontPayPerSlot: raw?.upfrontPayPerSlot,
    createdAt: raw?.createdAt,
    updatedAt: raw?.updatedAt,
    cases: Array.isArray(raw?.cases)
      ? raw.cases
          .filter((c: any) => c?.id)
          .map((c: any) => ({
            id: String(c.id),
            name: c.name ?? 'Case',
            image: c.image ?? '',
            itemImage: c.itemImage,
            price: Number(c.price ?? 0),
          }))
      : undefined,
    participants: Array.isArray(raw?.participants)
      ? raw.participants.map((participant: any) => extractArenaParticipant(participant))
      : undefined,
  }
}
